/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import React, { createContext, useContext, useReducer, useMemo } from "react"

// Types for the context
interface MaterialUIState {
  miniSidenav: boolean
  transparentSidenav: boolean
  whiteSidenav: boolean
  sidenavColor: string
  transparentNavbar: boolean
  fixedNavbar: boolean
  openConfigurator: boolean
  direction: "ltr" | "rtl"
  layout: string
  darkMode: boolean
}

type MaterialUIAction =
  | {
      type:
        | "MINI_SIDENAV"
        | "TRANSPARENT_SIDENAV"
        | "WHITE_SIDENAV"
        | "TRANSPARENT_NAVBAR"
        | "FIXED_NAVBAR"
        | "OPEN_CONFIGURATOR"
        | "DARKMODE"
      value: boolean
    }
  | {
      type: "SIDENAV_COLOR" | "LAYOUT"
      value: string
    }
  | {
      type: "DIRECTION"
      value: "ltr" | "rtl"
    }

type MaterialUIDispatch = React.Dispatch<MaterialUIAction>
interface MaterialUIContextType {
  state: MaterialUIState
  dispatch: MaterialUIDispatch
}

const initialState: MaterialUIState = {
  miniSidenav: false,
  transparentSidenav: false,
  whiteSidenav: false,
  sidenavColor: "info",
  transparentNavbar: true,
  fixedNavbar: true,
  openConfigurator: false,
  direction: "ltr",
  layout: "dashboard",
  darkMode: false
}

// Material Dashboard 2 React main context
const MaterialUI = createContext<MaterialUIContextType | null>(null)

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext"

// Material Dashboard 2 React reducer
const reducer: React.Reducer<MaterialUIState, MaterialUIAction> = (state, action) => {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value }
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value }
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value }
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value }
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value }
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value }
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value }
    }
    case "DIRECTION": {
      return { ...state, direction: action.value }
    }
    case "LAYOUT": {
      return { ...state, layout: action.value }
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value }
    }
    default: {
      throw new Error(`Unhandled action type: ${(action as MaterialUIAction).type}`)
    }
  }
}

// Material Dashboard 2 React context provider
interface MaterialUIControllerProviderProps {
  children: React.ReactNode
}

function MaterialUIControllerProvider({ children }: MaterialUIControllerProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const contextValue = useMemo<MaterialUIContextType>(
    () => ({
      state,
      dispatch: dispatch as MaterialUIDispatch
    }),
    [state, dispatch]
  )

  return <MaterialUI.Provider value={contextValue}>{children}</MaterialUI.Provider>
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController(): [MaterialUIState, MaterialUIDispatch] {
  const context = useContext(MaterialUI)

  if (!context) {
    throw new Error("useMaterialUIController should be used inside the MaterialUIControllerProvider.")
  }

  return [context.state, context.dispatch] as [MaterialUIState, MaterialUIDispatch]
}

// Context module functions
const setMiniSidenav = (dispatch: MaterialUIDispatch, value: boolean) => dispatch({ type: "MINI_SIDENAV", value })
const setTransparentSidenav = (dispatch: MaterialUIDispatch, value: boolean) =>
  dispatch({ type: "TRANSPARENT_SIDENAV", value })
const setWhiteSidenav = (dispatch: MaterialUIDispatch, value: boolean) => dispatch({ type: "WHITE_SIDENAV", value })
const setSidenavColor = (dispatch: MaterialUIDispatch, value: string) => dispatch({ type: "SIDENAV_COLOR", value })
const setTransparentNavbar = (dispatch: MaterialUIDispatch, value: boolean) =>
  dispatch({ type: "TRANSPARENT_NAVBAR", value })
const setFixedNavbar = (dispatch: MaterialUIDispatch, value: boolean) => dispatch({ type: "FIXED_NAVBAR", value })
const setOpenConfigurator = (dispatch: MaterialUIDispatch, value: boolean) =>
  dispatch({ type: "OPEN_CONFIGURATOR", value })
const setDirection = (dispatch: MaterialUIDispatch, value: "ltr" | "rtl") => dispatch({ type: "DIRECTION", value })
const setLayout = (dispatch: MaterialUIDispatch, value: string) => dispatch({ type: "LAYOUT", value })
const setDarkMode = (dispatch: MaterialUIDispatch, value: boolean) => dispatch({ type: "DARKMODE", value })

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode
}
